// Карточка человека в официальном контуре: история периодов, транши, документы, остатки.
//
// Тз, раздел 5.4.5, называет её среди четырёх экранов контура. Дверь на сервере была и
// работала, а из программы её не звал никто: официальная история человека собиралась глазами —
// переключением периодов в шапке и поиском фамилии в каждом. Вопрос «сколько Старцеву выплатили
// официально за год и по какому исполнительному документу» стоил десятка переключений.
//
// ## Почему периоды идут сверху вниз, от свежего к старому
//
// Спрашивают про последний. История нужна вторым вопросом, и листать её приходится реже, чем
// смотреть текущий остаток.

import type { OfficialPeriodOut, OfficialRowOut } from '@/shared/api/endpoints/official';
import { formatKopecks } from '@/shared/money/format';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';
import { Block } from '@/shared/ui/block';
import { FieldList, FieldRow } from '@/shared/ui/field-row';
import { Metric, MetricRow } from '@/shared/ui/metric';
import { ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { useEmployeeCircuit, usePeriods } from '../queries';

export function EmployeePanel({ id }: { id: string }) {
  const карточка = useEmployeeCircuit(id);
  const периоды = usePeriods();

  return (
    <ScreenState
      data={карточка.data}
      error={карточка.error}
      subject='официальную историю человека'
      isEmpty={() => false}
      onRetry={() => void карточка.refetch()}
      loading={<Skeleton className='h-56 w-full' />}
      empty={null}
    >
      {(человек) => {
        const названия = new Map(
          (периоды.data?.items ?? []).map((период: OfficialPeriodOut) => [период.id, период.title]),
        );
        const строки = [...человек.rows].sort((левая, правая) =>
          (названия.get(правая.official_period_id) ?? '').localeCompare(
            названия.get(левая.official_period_id) ?? '',
            'ru',
          ),
        );
        const действующие = человек.writ_orders.filter((документ) => документ.is_active);

        return (
          <div className='flex flex-col gap-4'>
            <span className='text-label-md text-text-strong-950'>{человек.employee_name}</span>

            <Block flush title='Сколько по контуру всего'>
              <MetricRow columns={2} className='p-4'>
                <Metric
                  tone='важный'
                  label='Всего к выдаче'
                  value={formatKopecks(человек.totals.payable)}
                  note='по всем периодам, после удержаний по исполнительным документам'
                />
                <Metric
                  tone={человек.totals.unsettled_total === 0 ? 'приглушённый' : 'тревожный'}
                  label='Непогашено'
                  value={formatKopecks(человек.totals.unsettled_total)}
                  note={
                    человек.totals.unsettled_total === 0
                      ? 'всё выдано'
                      : 'эти деньги переносятся из периода в период при закрытии'
                  }
                />
              </MetricRow>
            </Block>

            <Block flush title={`История периодов · ${countLabel(строки.length, ПЕРИОДЫ)}`}>
              {строки.length === 0 ? (
                <p className='p-4 text-paragraph-sm text-text-sub-600'>
                  В официальном контуре этого человека нет ни в одном периоде. Так бывает у тех,
                  кому официальная часть не начисляется вовсе: суммы в контур загружают из книги
                  заказчика, и его там нет.
                </p>
              ) : (
                <div className='px-4 pb-2'>
                  <FieldList>
                    {строки.map((строка) => (
                      <FieldRow
                        key={строка.id}
                        label={названия.get(строка.official_period_id) ?? 'период не нашёлся'}
                        value={описаниеСтроки(строка)}
                        note={пометкаСтроки(строка)}
                      />
                    ))}
                  </FieldList>
                </div>
              )}
            </Block>

            <Block flush title='Исполнительные документы'>
              {человек.writ_orders.length === 0 ? (
                <p className='p-4 text-paragraph-sm text-text-sub-600'>
                  Исполнительных документов на этого человека нет: удержаний приставов в контуре не
                  будет.
                </p>
              ) : (
                <div className='px-4 pb-2'>
                  <FieldList>
                    {человек.writ_orders.map((документ) => (
                      <FieldRow
                        key={документ.id}
                        label={
                          документ.document_number === null
                            ? 'Без номера'
                            : `№ ${документ.document_number}`
                        }
                        value={`${документ.percent} % · ${документ.basis}`}
                        note={
                          `с ${formatDateOnly(документ.date_from)}` +
                          (документ.date_to === null
                            ? ', без срока окончания'
                            : ` по ${formatDateOnly(документ.date_to)}`) +
                          (документ.is_active ? '' : ' · не действует')
                        }
                      />
                    ))}
                  </FieldList>
                </div>
              )}
              {действующие.length > 1 && (
                // Два действующих документа сразу — повод посмотреть: проценты складываются, и
                // человек может остаться без выплаты вовсе.
                <p className='px-4 pb-4 text-paragraph-xs text-warning-base'>
                  Действующих документов {действующие.length}: проценты складываются, проверьте, что
                  в сумме они не съедают всю официальную часть.
                </p>
              )}
            </Block>
          </div>
        );
      }}
    </ScreenState>
  );
}

const ПЕРИОДЫ = ['период', 'периода', 'периодов'] as const;

/** Что было в периоде: к выдаче, сколько выплачено и что осталось. */
function описаниеСтроки(строка: OfficialRowOut): string {
  const части = [`к выдаче ${formatKopecks(строка.payable)}`];
  if (строка.paid !== 0) {
    части.push(`выплачено ${formatKopecks(строка.paid)}`);
  }
  if (строка.balance !== 0) {
    части.push(
      строка.balance > 0
        ? `остаток ${formatKopecks(строка.balance)}`
        : `переплата ${formatKopecks(-строка.balance)}`,
    );
  }
  return части.join(' · ');
}

/** Пометка под строкой: состояние, удержание и перенос — то, чего в числах не видно. */
function пометкаСтроки(строка: OfficialRowOut): string {
  const части = [строка.state_title];
  if (строка.writ_percent !== null) {
    части.push(`удержано ${строка.writ_percent} % — ${formatKopecks(строка.writ_amount)}`);
  }
  if (строка.is_carried_in) {
    части.push('пришло переносом из прошлого периода');
  }
  if (строка.carried_out !== 0) {
    части.push(`ушло переносом ${formatKopecks(строка.carried_out)}`);
  }
  return части.join(' · ');
}
